import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Alert,
  Divider
} from '@mui/material';
import {
  Add,
  Delete,
  Close,
  Search,
  Create
} from '@mui/icons-material';
import { productService } from '../services/productService';

const OrderSummaryForm = ({ open, onClose, onConfirm, conversationId }) => {
  const [items, setItems] = useState([{ 
    productId: '', 
    quantity: 1, 
    price: 0, 
    isNewProduct: false,
    newProductName: '',
    newProductDescription: '',
    newProductCategory: '',
    newProductBrand: '',
    newProductDosage: ''
  }]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    city: '',
    state: '',
    country: 'Nigeria'
  });
  const [paymentMethod, setPaymentMethod] = useState('bank-transfer');
  const [notes, setNotes] = useState('');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderInterval, setReminderInterval] = useState({ value: 1, unit: 'weeks' });
  const [reminderMessage, setReminderMessage] = useState('');
  const [maxReminders, setMaxReminders] = useState('');

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getMyProducts();
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, { 
      productId: '', 
      quantity: 1, 
      price: 0, 
      isNewProduct: false,
      newProductName: '',
      newProductDescription: '',
      newProductCategory: '',
      newProductBrand: '',
      newProductDosage: ''
    }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // If product changed, update price
    if (field === 'productId') {
      const product = products.find(p => p._id === value);
      if (product) {
        newItems[index].price = product.price.nigeria || 0;
      }
    }
    
    // If switching to new product mode, clear productId
    if (field === 'isNewProduct' && value === true) {
      newItems[index].productId = '';
    }
    
    // If switching to existing product mode, clear new product fields
    if (field === 'isNewProduct' && value === false) {
      newItems[index].newProductName = '';
      newItems[index].newProductDescription = '';
      newItems[index].newProductCategory = '';
      newItems[index].newProductBrand = '';
      newItems[index].newProductDosage = '';
    }
    
    setItems(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDeliveryFee = () => {
    return deliveryAddress.country === 'Nigeria' ? 250 : 1;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateDeliveryFee();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.isNewProduct && !item.productId) {
          setError(`Please select a product for item ${i + 1}`);
          return;
        }
        if (item.isNewProduct && !item.newProductName.trim()) {
          setError(`Please enter a product name for item ${i + 1}`);
          return;
        }
      }
      
      if (!deliveryAddress.street || !deliveryAddress.city || !deliveryAddress.state) {
        setError('Please fill in the delivery address');
        return;
      }

      // Process items - create new products if needed
      const processedItems = [];
      
      for (const item of items) {
        if (item.isNewProduct) {
          // Check if product already exists by name
          const existingProduct = products.find(p => 
            p.name.toLowerCase() === item.newProductName.toLowerCase()
          );
          
          if (existingProduct) {
            // Use existing product
            processedItems.push({
              productId: existingProduct._id,
              quantity: item.quantity
            });
          } else {
            // Create new product
            const newProductData = {
              name: item.newProductName,
              description: item.newProductDescription,
              category: item.newProductCategory || 'general',
              brand: item.newProductBrand,
              dosage: item.newProductDosage,
              quantity: 100, // Default quantity
              unit: 'piece',
              'price.nigeria': item.price,
              'price.international': item.price * 250,
              isAvailable: true
            };
            
            const createdProduct = await productService.addProduct(newProductData);
            processedItems.push({
              productId: createdProduct._id,
              quantity: item.quantity
            });
          }
        } else {
          // Use existing product
          processedItems.push({
            productId: item.productId,
            quantity: item.quantity
          });
        }
      }

      const orderData = {
        items: processedItems,
        deliveryAddress,
        paymentMethod,
        notes,
        reminder: reminderEnabled ? {
          interval: reminderInterval,
          message: reminderMessage || `Time to refill your medication from order`,
          maxReminders: maxReminders ? parseInt(maxReminders) : null
        } : null
      };

      onConfirm(orderData);
    } catch (err) {
      console.error('Error creating order:', err);
      setError('Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setItems([{ 
      productId: '', 
      quantity: 1, 
      price: 0, 
      isNewProduct: false,
      newProductName: '',
      newProductDescription: '',
      newProductCategory: '',
      newProductBrand: '',
      newProductDosage: ''
    }]);
    setDeliveryAddress({
      street: '',
      city: '',
      state: '',
      country: 'Nigeria'
    });
    setPaymentMethod('bank_transfer');
    setNotes('');
    setReminderEnabled(false);
    setReminderInterval({ value: 1, unit: 'weeks' });
    setReminderMessage('');
    setMaxReminders('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Create Order Summary</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Order Items */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          Order Items
        </Typography>
        
        {items.map((item, index) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {/* Product Selection Mode Toggle */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant={!item.isNewProduct ? 'contained' : 'outlined'}
                size="small"
                startIcon={<Search />}
                onClick={() => handleItemChange(index, 'isNewProduct', false)}
              >
                Select Existing
              </Button>
              <Button
                variant={item.isNewProduct ? 'contained' : 'outlined'}
                size="small"
                startIcon={<Create />}
                onClick={() => handleItemChange(index, 'isNewProduct', true)}
              >
                Add New Product
              </Button>
            </Box>

            <Grid container spacing={2} alignItems="center">
              {!item.isNewProduct ? (
                // Existing Product Selection
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    select
                    label="Product"
                    value={item.productId}
                    onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                    size="small"
                    SelectProps={{
                      native: true
                    }}
                  >
                    <option value="">Select Product</option>
                    {products.map((product) => (
                      <option key={product._id} value={product._id}>
                        {product.name} - ₦{product.price.nigeria}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              ) : (
                // New Product Form
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Product Name *"
                      value={item.newProductName}
                      onChange={(e) => handleItemChange(index, 'newProductName', e.target.value)}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Brand"
                      value={item.newProductBrand}
                      onChange={(e) => handleItemChange(index, 'newProductBrand', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Category"
                      value={item.newProductCategory}
                      onChange={(e) => handleItemChange(index, 'newProductCategory', e.target.value)}
                      size="small"
                      select
                      SelectProps={{ native: true }}
                    >
                      <option value="">Select Category</option>
                      <option value="prescription">Prescription</option>
                      <option value="over-the-counter">Over-the-Counter</option>
                      <option value="supplements">Supplements</option>
                      <option value="medical-devices">Medical Devices</option>
                      <option value="personal-care">Personal Care</option>
                      <option value="general">General</option>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Dosage"
                      value={item.newProductDosage}
                      onChange={(e) => handleItemChange(index, 'newProductDosage', e.target.value)}
                      size="small"
                      placeholder="e.g., 500mg, 10ml"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={item.newProductDescription}
                      onChange={(e) => handleItemChange(index, 'newProductDescription', e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      placeholder="Brief description of the product..."
                    />
                  </Grid>
                </>
              )}
              
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={item.price}
                  onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                  size="small"
                  InputProps={{
                    startAdornment: '₦'
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Subtotal: ₦{(item.price * item.quantity).toLocaleString()}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, md: 1 }}>
                {items.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveItem(index)}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          startIcon={<Add />}
          onClick={handleAddItem}
          variant="outlined"
          size="small"
          sx={{ mb: 2 }}
        >
          Add Item
        </Button>

        <Divider sx={{ my: 2 }} />

        {/* Delivery Address */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Delivery Address
        </Typography>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Street Address"
              value={deliveryAddress.street}
              onChange={(e) => setDeliveryAddress({ ...deliveryAddress, street: e.target.value })}
              size="small"
              required
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="City"
              value={deliveryAddress.city}
              onChange={(e) => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })}
              size="small"
              required
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="State"
              value={deliveryAddress.state}
              onChange={(e) => setDeliveryAddress({ ...deliveryAddress, state: e.target.value })}
              size="small"
              required
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Payment Method */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Payment Method
        </Typography>
        
        <TextField
          fullWidth
          select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          size="small"
          SelectProps={{
            native: true
          }}
        >
          <option value="bank-transfer">Bank Transfer</option>
          <option value="cash">Cash</option>
          <option value="card">Card</option>
          <option value="mobile-money">Mobile Money</option>
        </TextField>

        <Divider sx={{ my: 2 }} />

        {/* Notes */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Notes (Optional)
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any special instructions for delivery..."
          size="small"
        />

        <Divider sx={{ my: 2 }} />

        {/* Reminder Settings */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Reminder Settings (Optional)
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <input
            type="checkbox"
            checked={reminderEnabled}
            onChange={(e) => setReminderEnabled(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <Typography variant="body2">
            Set up recurring reminders for this order
          </Typography>
        </Box>

        {reminderEnabled && (
          <Box sx={{ ml: 3, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Reminder Interval"
                  value={reminderInterval.value}
                  onChange={(e) => setReminderInterval(prev => ({ ...prev, value: parseInt(e.target.value) || 1 }))}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Time Unit"
                  value={reminderInterval.unit}
                  onChange={(e) => setReminderInterval(prev => ({ ...prev, unit: e.target.value }))}
                  size="small"
                  SelectProps={{ native: true }}
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </TextField>
              </Grid>
            </Grid>
            
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reminder Message"
              value={reminderMessage}
              onChange={(e) => setReminderMessage(e.target.value)}
              placeholder="e.g., Time to refill your medication"
              size="small"
              sx={{ mt: 2 }}
            />
            
            <TextField
              fullWidth
              type="number"
              label="Max Reminders (optional)"
              value={maxReminders}
              onChange={(e) => setMaxReminders(e.target.value)}
              placeholder="Leave empty for unlimited"
              size="small"
              sx={{ mt: 2 }}
              inputProps={{ min: 1 }}
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Order Summary */}
        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Order Summary
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Subtotal:</Typography>
            <Typography variant="body2">₦{calculateSubtotal().toLocaleString()}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Delivery Fee:</Typography>
            <Typography variant="body2">₦{calculateDeliveryFee().toLocaleString()}</Typography>
          </Box>
          
          <Divider sx={{ my: 1 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">Total:</Typography>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              ₦{calculateTotal().toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || items.length === 0}
        >
          {loading ? 'Creating...' : 'Create Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderSummaryForm;

